// Settings: notifications, what the AI may see, timing anchors, and export.
//
// The AI privacy control is a first-class setting rather than something buried, and
// it is worded in terms of what leaves the device — not in terms of features.

import { useCallback, useEffect, useState } from 'react';
import { api, type AiMode, type Settings as SettingsData } from '../lib/api.ts';
import {
  Button, Card, ErrorNote, Field, SectionTitle, Select, Spinner, TextInput,
} from '../components/ui.tsx';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push.ts';
import { getTheme, setTheme, type Theme } from '../lib/theme.ts';

const AI_MODE_LABELS: Record<AiMode, { label: string; detail: string }> = {
  off: {
    label: 'Off',
    detail: 'No data ever leaves this server. You still get every calculated figure, chart and report — just without the written summaries.',
  },
  structured_only: {
    label: 'Scores and adherence only',
    detail: 'Sends your metric names, numbers and adherence. Never your notes, and never uploaded documents.',
  },
  with_notes: {
    label: 'Scores plus my notes',
    detail: 'Also sends the notes you write, so summaries can reflect them. Documents are still never sent.',
  },
};

const ANCHOR_LABELS: Record<string, string> = {
  wake: 'Usually wake at',
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  bedtime: 'Bed',
  shower: 'Shower',
};

export function Settings({ onSignOut }: { onSignOut: () => void }) {
  const [data, setData] = useState<{
    settings: SettingsData;
    user: { name: string; email: string };
    server: { push_configured: boolean; ai_configured: boolean };
  } | null>(null);
  const [push, setPush] = useState<PushState>('off');
  const [pushBusy, setPushBusy] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [theme, setThemeState] = useState<Theme>(getTheme);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.settings.get());
      setPush(await pushState());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const update = async (patch: Partial<SettingsData>): Promise<void> => {
    if (!data) return;
    const { settings } = await api.settings.update(patch);
    setData({ ...data, settings });
  };

  if (error && !data) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const { settings, server } = data;

  return (
    <div className="space-y-6">
      <section>
        <SectionTitle>Notifications</SectionTitle>
        <Card className="space-y-3">
          {!server.push_configured && (
            <p className="text-sm text-serious">
              Push is not set up on this server. Run <code className="text-xs">npm run keys</code> and
              add the VAPID keys to <code className="text-xs">.env</code>.
            </p>
          )}

          {push === 'unsupported' && (
            <p className="text-sm text-ink-2">
              This browser cannot deliver notifications. On iPhone, add HealthLab to your home
              screen first — notifications only work from the installed app.
            </p>
          )}

          {push === 'denied' && (
            <p className="text-sm text-serious">
              Notifications are blocked in your browser settings for this site. You will need to
              re-allow them there before they can be turned on here.
            </p>
          )}

          {(push === 'on' || push === 'off') && (
            <>
              <p className="text-sm">
                Reminders on this device: <strong>{push === 'on' ? 'on' : 'off'}</strong>
              </p>
              <div className="flex flex-wrap gap-2">
                {push === 'off' ? (
                  <Button variant="primary" disabled={pushBusy} onClick={() => {
                    setPushBusy(true);
                    void enablePush().then(setPush).finally(() => setPushBusy(false));
                  }}>
                    Turn on for this device
                  </Button>
                ) : (
                  <>
                    <Button disabled={pushBusy} onClick={() => {
                      setPushBusy(true);
                      void api.push.test()
                        .then((r) => setTestResult(r.sent > 0 ? 'Sent — check your notifications.' : 'No device accepted it.'))
                        .finally(() => setPushBusy(false));
                    }}>
                      Send a test
                    </Button>
                    <Button variant="ghost" disabled={pushBusy} onClick={() => {
                      setPushBusy(true);
                      void disablePush().then(setPush).finally(() => setPushBusy(false));
                    }}>
                      Turn off
                    </Button>
                  </>
                )}
              </div>
              {testResult && <p className="text-xs text-muted">{testResult}</p>}
            </>
          )}

          <div className="pt-3 border-t border-hairline space-y-3">
            <Field label="Quiet hours" hint="Reminders still appear on Today — they just will not buzz.">
              <div className="flex items-center gap-2">
                <TextInput type="time" value={settings.quiet_hours_start ?? ''} onChange={(v) => void update({ quiet_hours_start: v || null })} />
                <span className="text-sm text-muted">to</span>
                <TextInput type="time" value={settings.quiet_hours_end ?? ''} onChange={(v) => void update({ quiet_hours_end: v || null })} />
              </div>
            </Field>
          </div>
        </Card>
      </section>

      <section>
        <SectionTitle>Your day</SectionTitle>
        <Card className="space-y-3">
          <p className="text-xs text-muted">
            Reminders set to "after dinner" or "before bed" use these times.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(settings.anchors).map(([anchor, time]) => (
              <Field key={anchor} label={ANCHOR_LABELS[anchor] ?? anchor}>
                <TextInput type="time" value={time}
                  onChange={(v) => void update({ anchors: { ...settings.anchors, [anchor]: v } as SettingsData['anchors'] })} />
              </Field>
            ))}
          </div>
        </Card>
      </section>

      <section>
        <SectionTitle>AI and privacy</SectionTitle>
        <Card className="space-y-3">
          {!server.ai_configured && (
            <p className="text-sm text-serious">
              No API key is configured on this server, so AI features are unavailable regardless of this setting.
            </p>
          )}
          <fieldset className="space-y-2">
            <legend className="sr-only">What may be sent to the AI</legend>
            {(Object.keys(AI_MODE_LABELS) as AiMode[]).map((mode) => (
              <label key={mode} className="flex gap-3 items-start p-3 rounded-xl border cursor-pointer"
                style={{
                  borderColor: settings.ai_mode === mode ? 'var(--color-accent)' : 'var(--color-hairline)',
                  background: settings.ai_mode === mode ? 'var(--color-accent-soft)' : 'transparent',
                }}>
                <input
                  type="radio"
                  name="ai_mode"
                  className="mt-1"
                  checked={settings.ai_mode === mode}
                  onChange={() => void update({ ai_mode: mode })}
                />
                <span>
                  <span className="block text-sm font-medium">{AI_MODE_LABELS[mode].label}</span>
                  <span className="block text-xs text-ink-2 mt-0.5 leading-relaxed">{AI_MODE_LABELS[mode].detail}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </Card>
      </section>

      <section>
        <SectionTitle>Appearance</SectionTitle>
        <Card>
          <Field label="Theme">
            <Select
              value={theme}
              onChange={(v) => { setTheme(v as Theme); setThemeState(v as Theme); }}
              options={[
                { value: 'system', label: 'Match my device' },
                { value: 'light', label: 'Light' },
                { value: 'dark', label: 'Dark' },
              ]}
            />
          </Field>
        </Card>
      </section>

      <section>
        <SectionTitle>Region</SectionTitle>
        <Card>
          <Field label="Timezone" hint="Reminders and daily dates use this.">
            <TextInput value={settings.timezone} onChange={(v) => void update({ timezone: v })} />
          </Field>
        </Card>
      </section>

      <section>
        <SectionTitle>Your data</SectionTitle>
        <Card className="space-y-3">
          <p className="text-sm text-ink-2">
            Everything is stored on this server. Nothing is shared with anyone, and no analytics
            of any kind are collected.
          </p>
          <a className="tap min-h-11 px-4 rounded-xl border border-line text-sm font-medium inline-flex items-center"
            href={api.settings.backupUrl} download>
            Download a full backup (JSON)
          </a>
        </Card>
      </section>

      <section>
        <SectionTitle>Account</SectionTitle>
        <Card className="space-y-3">
          <p className="text-sm">{data.user.name} · {data.user.email}</p>
          <Button variant="ghost" onClick={onSignOut}>Sign out</Button>
        </Card>
      </section>
    </div>
  );
}
